import { ICON_BASE_PATH } from "@/app/utils/config";
 
type GxIconButtonType =
  | "edit"
  | "editv2"
  | "delete"
  | "add"
  | "addb"
  | "view"
  | "download"
  | "minus";
type GxIconButtonSize = "xsmall" | "small" | "medium" | "large";
type GxIconButtonVariant = "default";
 
interface GxIconButtonProps {
  type: GxIconButtonType;
  disabled?: boolean;
  onClick?: () => void;
  alt?: string;
  title?: string;
  className?: string;
  size?: GxIconButtonSize;
  variant?: GxIconButtonVariant;
  loading?: boolean;
  loadingContent?: React.ReactNode;
}
 
const sizeClassMap: Record<GxIconButtonSize, string> = {
  xsmall: "gx-icon-btn-xsmall",
  small: "gx-icon-btn-small",
  medium: "gx-icon-btn-medium",
  large: "gx-icon-btn-large",
};
 
// `disabled` is now OPTIONAL per icon type. Only set it when a distinct
// disabled-state asset actually exists. Types without one will fall back
// to a dimmed version of the active icon (see opacity logic below).
const iconMap: Record<GxIconButtonType, { active: string; disabled?: string }> = {
  edit: {
    active: "edite-icon.svg",
    disabled: "disabled-edite-icon.svg",
  },
  editv2: {
    active: "editicon2.svg",
    // no dedicated disabled asset — opacity fallback applies
  },
  delete: {
    active: "delete-icon.svg",
    disabled: "disabled-delete-icon.svg",
  },
  add: {
    active: "plus.svg",

  },
  addb: {
    active: "plus-icon-b.svg",
    disabled: "plus-dim-icon.svg",
  },
  view: {
    active: "eyeicon.svg",
  },
  download: {
    active: "downloadicon.svg",
  },
  minus: {
    active: "minus.svg",
    // no dedicated disabled asset — opacity fallback applies
  },
};
 
const GxIconButton = ({
  type,
  disabled = false,
  onClick,
  alt,
  title,
  className = "",
  size = "medium",
  variant = "default",
  loading = false,
  loadingContent,
}: GxIconButtonProps) => {
  if (loading) {
    return (
      <div className="gx-stmt-download-loading" role="status" aria-live="polite">
        {loadingContent ?? <span>...</span>}
      </div>
    );
  }
 
  const iconEntry = iconMap[type];
  const hasDisabledIcon = Boolean(iconEntry.disabled);
  const shouldUseDisabledIcon = disabled && hasDisabledIcon;
 
  // Only dim via opacity when disabled AND there's no dedicated disabled icon
  const needsOpacityFallback = disabled && !hasDisabledIcon;
 
  const accessibleName = title || alt || `${type} icon`;
 
  return (
    <button
      type="button"
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={accessibleName}
      title={accessibleName}
    >
      <img
        src={`${ICON_BASE_PATH}${
          shouldUseDisabledIcon ? iconEntry.disabled : iconEntry.active
        }`}
        // alt="" because the button already carries the accessible name
        // via aria-label/title — avoids double announcement by screen readers
        alt=""
        aria-hidden="true"
        className={`
          gx-auth-matrix-icon
          ${sizeClassMap[size]}
          gx-icon-btn-${variant}
          ${!disabled ? "cursor-pointer" : ""}
          ${needsOpacityFallback ? "gx-icon-btn-opacity-disabled" : ""}
          ${className}
        `}
      />
    </button>
  );
};
 
export default GxIconButton;